/** Per-date availability projection consumed by the month-tile aggregator. */
export interface MaldivesCharterMonthTileDateAvailability {
  /** Calendar key for the date (typically `checkIn` in `YYYY-MM-DD`). */
  dateKey: string
  /** Result of the per-date all-or-nothing aggregation across legs. */
  allLegsAvailable: boolean
}

export interface MaldivesCharterMonthTileRewardPricingArgs {
  /** Month → ordered list of date availability results in that month. */
  datesByMonth: Readonly<Record<string, ReadonlyArray<MaldivesCharterMonthTileDateAvailability>>>
}

export interface MaldivesCharterMonthTileRewardPricingResult {
  /** True when at least one date in the month has all legs available — drives the month-tile reward seal. */
  showRewardSeal: boolean
  /** Subset of input dates in the month that contribute to the seal (`allLegsAvailable === true`). */
  availableDateKeys: ReadonlyArray<string>
}

/**
 * Aggregates per-date Classic Rewards availability into per-month-tile state for the Maldives charter calendar.
 *
 * The month-tile reward seal is shown when at least one date in the month has every leg available
 * (all-or-nothing per date — return-trip = 2 legs, multi-city = N legs). Replaces the prototype's
 * pseudo-random `shouldShowMaldivesCharterRewardMonthTileIcon`.
 */
export function aggregateMaldivesCharterMonthTileRewardPricing(
  args: MaldivesCharterMonthTileRewardPricingArgs,
): Record<string, MaldivesCharterMonthTileRewardPricingResult> {
  const out: Record<string, MaldivesCharterMonthTileRewardPricingResult> = {}
  for (const [month, dates] of Object.entries(args.datesByMonth)) {
    const availableDateKeys: Array<string> = []
    for (const date of dates) {
      if (date.allLegsAvailable) {
        availableDateKeys.push(date.dateKey)
      }
    }
    out[month] = {
      showRewardSeal: availableDateKeys.length > 0,
      availableDateKeys,
    }
  }
  return out
}
